import React from 'react';

import { createStyles, LoadingOverlay, Paper, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import usePaymentMethod from 'hooks/paymentMethod/usePaymentMethod';
import { AthletePackageRetrieve } from 'types';
import { parseDateTimeToDate } from 'utils/date-functions';
import { getPaymentMethodName } from 'utils/formatters';

interface IBillingSummaryCardProps {
  athletePackage: AthletePackageRetrieve;
}

const useStyles = createStyles(theme => ({
  labelGrid: {
    display: 'grid',
    rowGap: theme.spacing.xs,
    columnGap: theme.spacing.md,
    alignItems: 'center',
    gridTemplateColumns: 'min-content auto',
    gridTemplateRows: 'auto',
    whiteSpace: 'nowrap',
    [theme.fn.smallerThan('sm')]: {
      fontSize: theme.fontSizes.sm,
    },
  },
}));

const BillingSummaryCard = ({ athletePackage }: IBillingSummaryCardProps): JSX.Element => {
  const { classes, theme } = useStyles();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);
  const { dateFormat } = useLocalization();
  const { session } = useAuth();

  const { data, isSuccess, isLoading } = usePaymentMethod(
    athletePackage.paymentMethod ? athletePackage.paymentMethod : undefined,
  );

  const defaultPayment = (): string => {
    if (athletePackage.paymentMethod && isSuccess && data?.data?.metadata) {
      return getPaymentMethodName(data.data);
    } else {
      return athletePackage.nonStripePaymentMethod || 'Cash or Check';
    }
  };

  return (
    <Paper px='lg' py='md' shadow='xs' radius='md' withBorder>
      <Text size={isDesktop ? 'lg' : 'md'} fw='bold' mb='xs'>
        Billing
      </Text>
      <LoadingOverlay visible={isLoading} />
      <div className={classes.labelGrid}>
        <Text color='dimmed' fs='italic' fz='sm' ta='right'>
          Payment Method
        </Text>
        <Text ta='left'>{defaultPayment()}</Text>

        <Text color='dimmed' fs='italic' fz='sm' ta='right'>
          Billing Plan
        </Text>
        <Text ta='left'>{athletePackage.billingPlan.name}</Text>

        <Text color='dimmed' fs='italic' fz='sm' ta='right'>
          Billing Start Date
        </Text>
        <Text ta='left'>
          {dayjs(parseDateTimeToDate(athletePackage.billingStartDate, session?.gym?.timezone)).format(
            dateFormat,
          )}
        </Text>

        <Text color='dimmed' fs='italic' fz='sm' ta='right'>
          Auto Pay
        </Text>
        <Text ta='left'>{athletePackage.autoPay ? 'On' : 'Off'}</Text>
      </div>
    </Paper>
  );
};

export default BillingSummaryCard;
